import React, { useState, useEffect, useMemo } from "react";
import Icon from "./Icon";
import Modal from "./Modal";
import { StatusBadge } from "./Badge";
import Skeleton from "./Skeleton";
import { EmptyState, ErrorState } from "./State";
import SearchInput from "./SearchInput";
import Pagination from "./Pagination";
import {
  fetchServers,
  fetchServerServices,
  attachServicesToApplication,
} from "../modules/ops/services/CMDBService";

export default function ServerServicePickerModal({
  targetApp,
  onClose,
  onAttached,
}) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vmSearch, setVmSearch] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");
  const [selectedServer, setSelectedServer] = useState(null);
  const [serverServices, setServerServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Pagination for VM list: Enterprise page size of 8 servers with internal scrolling
  const [vmPage, setVmPage] = useState(1);
  const VM_PAGE_SIZE = 8;

  // Stored as map: { [serviceId]: { service, server } } for cross-server selection
  const [selectedServicesMap, setSelectedServicesMap] = useState({});
  const [relationType, setRelationType] = useState("owner");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadServers() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchServers({ limit: 100, offset: 0 });
      const items = data?.items || [];
      setServers(items);
      if (items.length > 0) {
        handleSelectServer(items[0]);
      }
    } catch (err) {
      setError("Failed to load servers.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSelectServer(server) {
    setSelectedServer(server);
    setLoadingServices(true);
    setServiceSearch("");
    try {
      const svcs = await fetchServerServices(server.id);
      setServerServices(svcs || []);
    } catch (err) {
      setServerServices([]);
    } finally {
      setLoadingServices(false);
    }
  }

  function toggleServiceSelection(svc, server) {
    const currentServer = server || selectedServer;
    setSelectedServicesMap((prev) => {
      const next = { ...prev };
      if (next[svc.id]) {
        delete next[svc.id];
      } else {
        next[svc.id] = { service: svc, server: currentServer };
      }
      return next;
    });
  }

  function toggleSelectAllOnServer(filteredSvcs) {
    const svcsToToggle = filteredSvcs || serverServices;
    if (!selectedServer || svcsToToggle.length === 0) return;
    const allSelectedOnThisServer = svcsToToggle.every(
      (s) => !!selectedServicesMap[s.id]
    );

    setSelectedServicesMap((prev) => {
      const next = { ...prev };
      if (allSelectedOnThisServer) {
        svcsToToggle.forEach((s) => {
          delete next[s.id];
        });
      } else {
        svcsToToggle.forEach((s) => {
          next[s.id] = { service: s, server: selectedServer };
        });
      }
      return next;
    });
  }

  function handleRemoveSelectedService(serviceId) {
    setSelectedServicesMap((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
  }

  function handleClearAllSelected() {
    setSelectedServicesMap({});
  }

  async function handleAttach() {
    const serviceIds = Object.keys(selectedServicesMap);
    if (serviceIds.length === 0 || !targetApp) return;
    setSubmitting(true);
    try {
      await attachServicesToApplication(
        targetApp.id,
        serviceIds,
        relationType
      );
      if (onAttached) onAttached();
      onClose();
    } catch (err) {
      alert("Failed to link services: " + (err.message || "Unknown error"));
    } finally {
      setSubmitting(false);
    }
  }

  const selectedList = useMemo(() => Object.values(selectedServicesMap), [selectedServicesMap]);
  const distinctServerCount = useMemo(() => {
    const srvIds = new Set(selectedList.map((item) => item.server?.id).filter(Boolean));
    return srvIds.size;
  }, [selectedList]);

  // Count of selected services per server ID for badges
  const serverSelectedCounts = useMemo(() => {
    const counts = {};
    selectedList.forEach((item) => {
      if (item.server?.id) {
        counts[item.server.id] = (counts[item.server.id] || 0) + 1;
      }
    });
    return counts;
  }, [selectedList]);

  const filteredServers = useMemo(() => {
    const term = (vmSearch || "").toLowerCase();
    return servers.filter(
      (s) =>
        s.hostname.toLowerCase().includes(term) ||
        (s.name && s.name.toLowerCase().includes(term)) ||
        (s.os && s.os.toLowerCase().includes(term)) ||
        (s.specs?.ip_address && s.specs.ip_address.includes(term))
    );
  }, [servers, vmSearch]);

  const filteredServices = useMemo(() => {
    const term = (serviceSearch || "").toLowerCase();
    if (!term) return serverServices;
    return serverServices.filter((svc) => {
      const port =
        svc.metadata_json?.port ||
        svc.specs?.port ||
        (svc.name.includes(":") ? svc.name.split(":")[1] : null);
      return (
        svc.name.toLowerCase().includes(term) ||
        (svc.service_type && svc.service_type.toLowerCase().includes(term)) ||
        (port && String(port).includes(term))
      );
    });
  }, [serverServices, serviceSearch]);

  useEffect(() => {
    setVmPage(1);
  }, [vmSearch]);

  const paginatedServers = useMemo(() => {
    const start = (vmPage - 1) * VM_PAGE_SIZE;
    return filteredServers.slice(start, start + VM_PAGE_SIZE);
  }, [filteredServers, vmPage, VM_PAGE_SIZE]);

  return (
    <Modal
      title={`Link Services to ${targetApp?.name || "Application"}`}
      onClose={onClose}
      width={1020}
      bodyStyle={{ padding: "16px 22px", display: "flex", flexDirection: "column" }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: 600,
          gap: 12,
        }}
      >
        {/* Top Info Banner & Service Role Selector */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 14px",
            background: "#F8FAFC",
            borderRadius: 8,
            border: "1px solid #E2E8F0",
            flexWrap: "wrap",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>
              Target Application:{" "}
              <span style={{ color: "#1D4ED8", fontWeight: 700 }}>
                {targetApp?.name}
              </span>
              {targetApp?.code && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 11,
                    padding: "2px 6px",
                    background: "#E2E8F0",
                    borderRadius: 4,
                    color: "#475569",
                    fontWeight: 600,
                  }}
                >
                  {targetApp.code}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 2 }}>
              Select listening port services across one or multiple host servers to link to this application.
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
              Service Role:
            </label>
            <select
              value={relationType}
              onChange={(e) => setRelationType(e.target.value)}
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                border: "1px solid #CBD5E1",
                fontSize: 12,
                background: "#FFFFFF",
                fontWeight: 500,
                color: "#1E293B",
                cursor: "pointer",
              }}
            >
              <option value="owner">Application Component (Host Service)</option>
              <option value="consumer">External Dependency (Client Service)</option>
            </select>
          </div>
        </div>

        {/* Middle 2-Column Split */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "330px 1fr",
            gap: 16,
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Left Column: Server Search, Scrollable List (8 per page) & VM Pagination */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              minHeight: 0,
            }}
          >
            {/* Search Input */}
            <SearchInput
              size="sm"
              placeholder="Search host VMs & servers..."
              value={vmSearch}
              onChange={setVmSearch}
            />

            {/* Scrollable Server Cards Viewport */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 6,
                paddingRight: 4,
              }}
            >
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} height={56} style={{ borderRadius: 6 }} />
                ))
              ) : error ? (
                <ErrorState message={error} />
              ) : paginatedServers.length === 0 ? (
                <EmptyState title="No servers found" />
              ) : (
                paginatedServers.map((server) => {
                  const isSelected = selectedServer?.id === server.id;
                  const countOnServer = serverSelectedCounts[server.id] || 0;

                  return (
                    <div
                      key={server.id}
                      onClick={() => handleSelectServer(server)}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 6,
                        border: isSelected
                          ? "2px solid #2563EB"
                          : countOnServer > 0
                          ? "1px solid #86EFAC"
                          : "1px solid #E2E8F0",
                        background: isSelected
                          ? "#EFF6FF"
                          : countOnServer > 0
                          ? "#F0FDF4"
                          : "#FFFFFF",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        boxSizing: "border-box",
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 12.5,
                            color: isSelected ? "#1D4ED8" : "#0F172A",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                          title={server.hostname}
                        >
                          {server.hostname}
                        </span>
                        {countOnServer > 0 && (
                          <span
                            style={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              background: "#DCFCE7",
                              color: "#15803D",
                              padding: "1px 6px",
                              borderRadius: 10,
                              whiteSpace: "nowrap",
                              border: "1px solid #BBF7D0",
                            }}
                          >
                            {countOnServer} selected
                          </span>
                        )}
                        <StatusBadge entity={server} type="server" />
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#64748B",
                          marginTop: 3,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 160,
                          }}
                        >
                          {server.os || "Linux"}
                        </span>
                        <span style={{ fontFamily: "monospace", fontSize: 10.5 }}>
                          {server.specs?.ip_address || "—"}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Enterprise VM Pagination Controls */}
            {filteredServers.length > 0 && (
              <Pagination
                page={vmPage}
                total={filteredServers.length}
                pageSize={VM_PAGE_SIZE}
                onChange={setVmPage}
                variant="compact"
                itemLabel="VMs"
              />
            )}
          </div>

          {/* Right Column: Discovered Services on Selected Host */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              minHeight: 0,
              borderLeft: "1px solid #E2E8F0",
              paddingLeft: 16,
            }}
          >
            {/* Services Header, Filter Search & Select All */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #E2E8F0",
                paddingBottom: 6,
                flexShrink: 0,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1E293B" }}>
                  Services on{" "}
                  <span style={{ color: "#1D4ED8", fontWeight: 700 }}>
                    {selectedServer?.hostname || "..."}
                  </span>
                </div>
                {serverServices.length > 0 && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      background: "#F1F5F9",
                      color: "#475569",
                      padding: "1px 6px",
                      borderRadius: 10,
                    }}
                  >
                    {serverServices.length} total
                  </span>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {serverServices.length > 5 && (
                  <input
                    type="text"
                    placeholder="Filter port/service..."
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    style={{
                      padding: "3px 8px",
                      borderRadius: 5,
                      border: "1px solid #CBD5E1",
                      fontSize: 11,
                      width: 140,
                    }}
                  />
                )}
                {filteredServices.length > 0 && (
                  <button
                    type="button"
                    onClick={() => toggleSelectAllOnServer(filteredServices)}
                    style={{
                      fontSize: 11.5,
                      color: "#1D4ED8",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    {filteredServices.every((s) => !!selectedServicesMap[s.id])
                      ? "Deselect All on Host"
                      : "Select All on Host"}
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable Services List Viewport */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 6,
                paddingRight: 4,
              }}
            >
              {loadingServices ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} height={48} style={{ borderRadius: 6 }} />
                ))
              ) : filteredServices.length === 0 ? (
                <EmptyState
                  title={serviceSearch ? "No matching services" : "No services detected"}
                  subtitle={
                    serviceSearch
                      ? "No services matched your filter keyword."
                      : "This host has no active port or process services registered."
                  }
                />
              ) : (
                filteredServices.map((svc) => {
                  const isChecked = !!selectedServicesMap[svc.id];
                  const port =
                    svc.metadata_json?.port ||
                    svc.specs?.port ||
                    (svc.name.includes(":") ? svc.name.split(":")[1] : null);

                  return (
                    <div
                      key={svc.id}
                      onClick={() => toggleServiceSelection(svc, selectedServer)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 12px",
                        borderRadius: 6,
                        border: isChecked
                          ? "1.5px solid #3B82F6"
                          : "1px solid #E2E8F0",
                        background: isChecked ? "#EFF6FF" : "#FAFAFA",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {}}
                        style={{ cursor: "pointer" }}
                      />

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 12.5,
                              color: "#0F172A",
                            }}
                          >
                            {svc.name}
                          </span>
                          {port && (
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#0284C7",
                                background: "#E0F2FE",
                                padding: "1px 6px",
                                borderRadius: 4,
                                fontFamily: "monospace",
                              }}
                            >
                              :{port}
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: 10.5,
                              color: "#64748B",
                              background: "#F1F5F9",
                              padding: "1px 6px",
                              borderRadius: 4,
                            }}
                          >
                            {svc.service_type || "process"}
                          </span>
                        </div>
                        {svc.version && (
                          <div style={{ fontSize: 10.5, color: "#94A3B8", marginTop: 2 }}>
                            v{svc.version}
                          </div>
                        )}
                      </div>

                      <StatusBadge entity={svc} type="service" />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Staged Services Tray (Cross-Host Basket) */}
        {selectedList.length > 0 && (
          <div
            style={{
              padding: "8px 12px",
              background: "#F0FDF4",
              borderRadius: 6,
              border: "1px solid #BBF7D0",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              flexShrink: 0,
              maxHeight: 84,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 11.5, fontWeight: 700, color: "#166534" }}>
                Staged for Attachment ({selectedList.length} across {distinctServerCount} server{distinctServerCount > 1 ? "s" : ""}):
              </span>
              <button
                type="button"
                onClick={handleClearAllSelected}
                style={{
                  fontSize: 11,
                  color: "#DC2626",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Clear All
              </button>
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                overflowY: "auto",
                maxHeight: 50,
              }}
            >
              {selectedList.map(({ service, server }) => (
                <span
                  key={service.id}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "2px 7px",
                    borderRadius: 4,
                    background: "#DCFCE7",
                    color: "#14532D",
                    fontSize: 11,
                    fontWeight: 600,
                    border: "1px solid #86EFAC",
                  }}
                >
                  <span style={{ color: "#166534", fontWeight: 700 }}>
                    {server?.hostname ? server.hostname.split(".")[0] : "Host"}:
                  </span>
                  <span>{service.name}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveSelectedService(service.id);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#166534",
                      cursor: "pointer",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      marginLeft: 2,
                    }}
                    title="Remove"
                  >
                    <Icon name="x" size={11} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 10,
            borderTop: "1px solid #E2E8F0",
            flexShrink: 0,
          }}
        >
          <div style={{ fontSize: 11.5, color: "#64748B" }}>
            <strong style={{ color: "#0F172A" }}>
              {selectedList.length}
            </strong>{" "}
            services selected across{" "}
            <strong style={{ color: "#0F172A" }}>
              {distinctServerCount}
            </strong>{" "}
            host server{distinctServerCount !== 1 ? "s" : ""}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "7px 14px",
                borderRadius: 6,
                border: "1px solid #CBD5E1",
                background: "#FFFFFF",
                fontSize: 12.5,
                fontWeight: 500,
                color: "#475569",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={selectedList.length === 0 || submitting}
              onClick={handleAttach}
              style={{
                padding: "7px 16px",
                borderRadius: 6,
                border: "none",
                background:
                  selectedList.length === 0 || submitting
                    ? "#94A3B8"
                    : "#1D4ED8",
                color: "#FFFFFF",
                fontSize: 12.5,
                fontWeight: 600,
                cursor:
                  selectedList.length === 0 || submitting
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Icon name="link" size={13} />
              {submitting
                ? "Linking..."
                : `Link Services (${selectedList.length})`}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
